import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api";
import type {
  Partner,
  PartnerListItem,
  PartnerPayout,
  PartnersListResponse,
} from "../../shared/types";
import { money, num } from "../lib/format";
import { AddPartnerModal } from "../components/AddPartnerModal";
import { ProgramSettingsModal } from "../components/ProgramSettingsModal";
import { useNative } from "../../shared/phone/media";
import { PartnersScreen } from "./partners/PartnersScreen";
import { copiedNote, SayToast } from "./partners/parts";
import { ProgramScreens } from "./partners/ProgramScreens";
import "../styles/partners.css";

type Tab = "active" | "pending";

/** Two-letter monogram from a partner's name (first + last initial). */
function initials(first: string, last: string): string {
  const a = first.trim().charAt(0);
  const b = last.trim().charAt(0);
  return (a + b).toUpperCase() || "?";
}

function fullName(first: string, last: string): string {
  return `${first} ${last}`.trim();
}

/** The partner's referral link, Flashadz style (/go?p=slug). */
function referralPath(slug: string): string {
  return `/go?p=${slug}`;
}

/** "Aug 1" from a 'YYYY-MM-DD HH:MM:SS' stamp; empty when unparseable. */
function shortDate(stamp: string | null): string {
  if (!stamp) return "";
  const d = new Date(stamp.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function BackIcon() {
  return (
    <svg className="bk-ic" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="s-ic" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20.5 20.5l-4-4" />
    </svg>
  );
}

/** A partner's payout position as one status pill, matching the pay run wording. */
function StatusPill({ payout }: { payout: PartnerPayout }) {
  if (payout.state === "ready") {
    return <span className="pill due">{money(payout.readyCents)} ready</span>;
  }
  if (payout.state === "blocked") {
    return (
      <span className="pill pending" title={payout.reason ?? undefined}>
        On hold
      </span>
    );
  }
  if (payout.state === "below_min") {
    return (
      <span className="pill pending" title={payout.reason ?? undefined}>
        Below minimum
      </span>
    );
  }
  // clear: nothing owed. Show the last recorded payment when there is one.
  if (payout.lastPaidAt) {
    return <span className="pill paidout">Paid {shortDate(payout.lastPaidAt)}</span>;
  }
  return <span className="pill pending">Nothing due</span>;
}

/** One active-partner row: monogram, link, click/reg/sale counts, earned, status. Opens the partner detail. */
function ActiveRow({ item, onOpen }: { item: PartnerListItem; onOpen: (id: string) => void }) {
  const p = item.partner;
  const k = item.kpis;
  return (
    <tr
      className="rowlink"
      tabIndex={0}
      role="link"
      aria-label={`Open ${fullName(p.firstName, p.lastName)}`}
      onClick={() => onOpen(p.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(p.id);
        }
      }}
    >
      <td>
        <div className="pa">
          <span className="av">{initials(p.firstName, p.lastName)}</span>
          <div>
            <div className="pn">
              {fullName(p.firstName, p.lastName)}
              {p.selfReferralFlag && <span className="flag">self-referral</span>}
            </div>
            <div className="pl">{referralPath(p.slug)}</div>
          </div>
        </div>
      </td>
      <td className="num">{num(k.clicks)}</td>
      <td className="num">{num(k.regs)}</td>
      <td className="num">{num(k.sales)}</td>
      <td className="num earn">{money(k.earnedCents)}</td>
      <td>
        <StatusPill payout={item.payout} />
      </td>
    </tr>
  );
}

/** One pending-applicant row: applicant, when they applied, agreement, actions. */
function PendingRow({
  item,
  busy,
  onApprove,
  onReject,
}: {
  item: PartnerListItem;
  busy: boolean;
  onApprove: (item: PartnerListItem) => void;
  onReject: (item: PartnerListItem) => void;
}) {
  const p = item.partner;
  return (
    <tr>
      <td>
        <div className="pa">
          <span className="av">{initials(p.firstName, p.lastName)}</span>
          <div>
            <div className="pn">{fullName(p.firstName, p.lastName)}</div>
            <div className="pl">{p.email}</div>
          </div>
        </div>
      </td>
      <td className="num">{shortDate(p.appliedAt) || "recently"}</td>
      <td>
        {item.documents.agreement ? (
          <span className="pill paidout">Signed</span>
        ) : (
          <span className="pill pending">Not signed</span>
        )}
      </td>
      <td className="acts">
        <button
          type="button"
          className="btn ghost sm"
          disabled={busy}
          onClick={() => onReject(item)}
        >
          Reject
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={() => onApprove(item)}>
          Approve
        </button>
      </td>
    </tr>
  );
}

/** Owner partners list (`/products/:id/partners`): KPIs, ledger note, both tables. */
export function Partners() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const native = useNative();
  const [params] = useSearchParams();

  const [data, setData] = useState<PartnersListResponse | null>(null);
  const [productName, setProductName] = useState<string | null>(null);
  const [productSlug, setProductSlug] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("active");
  // Search filters the loaded rows for real (client-side). The prototype also
  // shows a date range, but the list KPIs + counters are lifetime aggregates and
  // GET /products/:id/partners takes no range, so a date filter here would be
  // fake. Deferred until S1 exposes a windowed partner list. See GRAPH-v2 U0.
  // U0: partner-list date range = lifetime for now (no windowed API); confirm.
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [toast, setToast] = useState<{ msg: string; color: string; show: boolean }>({
    msg: "",
    color: "var(--violet)",
    show: false,
  });
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string, color = "var(--violet)") => {
    setToast({ msg, color, show: true });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2800);
  }, []);
  useEffect(() => () => void (toastTimer.current && window.clearTimeout(toastTimer.current)), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    setNotFound(false);
    setData(null);
    setProductName(null);
    setProductSlug(null);
    api.partners
      .list(id)
      .then((d) => alive && setData(d))
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setErr(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => alive && setLoading(false));
    // Product name + slug feed the eyebrow, back label, and the invite join link.
    // Best-effort, never blocks the list.
    api.products
      .detail(id)
      .then((pd) => {
        if (!alive) return;
        setProductName(pd.product.name);
        setProductSlug(pd.product.slug);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id]);

  const reload = useCallback(async () => {
    const d = await api.partners.list(id);
    setData(d);
  }, [id]);

  const approve = useCallback(
    async (item: PartnerListItem) => {
      if (busyId) return;
      const pid = item.partner.id;
      const name = fullName(item.partner.firstName, item.partner.lastName);
      setBusyId(pid);
      // Optimistic: move the applicant into Active now, reconcile with the server.
      setData((prev) =>
        prev
          ? {
              active: [
                { ...item, partner: { ...item.partner, status: "active" } },
                ...prev.active,
              ],
              pending: prev.pending.filter((x) => x.partner.id !== pid),
            }
          : prev,
      );
      showToast(`${name} approved`, "var(--win)");
      try {
        await api.partners.approve(pid);
        await reload();
      } catch (e) {
        await reload().catch(() => {});
        showToast(
          `Couldn't approve ${name}: ${e instanceof ApiError ? e.message : String(e)}`,
          "var(--warn)",
        );
      } finally {
        setBusyId(null);
      }
    },
    [busyId, reload, showToast],
  );

  const reject = useCallback(
    async (item: PartnerListItem) => {
      if (busyId) return;
      const pid = item.partner.id;
      const name = fullName(item.partner.firstName, item.partner.lastName);
      setBusyId(pid);
      // Optimistic: drop the applicant now, reconcile with the server.
      setData((prev) =>
        prev ? { ...prev, pending: prev.pending.filter((x) => x.partner.id !== pid) } : prev,
      );
      showToast(`${name} rejected`, "var(--dim)");
      try {
        await api.partners.reject(pid);
        await reload();
      } catch (e) {
        await reload().catch(() => {});
        showToast(
          `Couldn't reject ${name}: ${e instanceof ApiError ? e.message : String(e)}`,
          "var(--warn)",
        );
      } finally {
        setBusyId(null);
      }
    },
    [busyId, reload, showToast],
  );

  const totals = useMemo(() => {
    if (!data) return null;
    const all = [...data.active, ...data.pending];
    return {
      activeCount: data.active.length,
      pendingCount: data.pending.length,
      regs: all.reduce((s, p) => s + p.kpis.regs, 0),
      sales: all.reduce((s, p) => s + p.kpis.sales, 0),
      owedCents: data.active.reduce((s, p) => s + p.payout.owedCents, 0),
      readyCents: data.active.reduce((s, p) => s + p.payout.readyCents, 0),
    };
  }, [data]);

  const filterRows = useCallback(
    (rows: PartnerListItem[]): PartnerListItem[] => {
      const q = query.trim().toLowerCase();
      if (!q) return rows;
      return rows.filter((r) => {
        const p = r.partner;
        return (
          fullName(p.firstName, p.lastName).toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q)
        );
      });
    },
    [query],
  );

  const name = productName ?? "your product";
  const backLabel = productName ?? "Product";
  const openPayrun = () => navigate(`/products/${encodeURIComponent(id)}/payrun`);
  const openSettings = () => setSettingsOpen(true);
  const openInvite = () => setInviteOpen(true);
  // Edit opens the agreement builder (node B11) for this product; carry the
  // product name so the paper titles correctly without an extra fetch.
  const openAgreementBuilder = () => {
    setSettingsOpen(false);
    navigate(`/products/${encodeURIComponent(id)}/agreement`, { state: { productName } });
  };
  const openLibrary = () => {
    setSettingsOpen(false);
    navigate(`/products/${encodeURIComponent(id)}`);
  };

  // A real invite landed (POST /products/:id/partners): reload so the new partner
  // shows, and surface the Active tab where owner-invited partners land.
  const onInvited = useCallback(
    (partner: Partner) => {
      setTab("active");
      showToast(`${fullName(partner.firstName, partner.lastName)} invited`, "var(--win)");
      reload().catch(() => {});
    },
    [reload, showToast],
  );

  if (native) {
    // Why: Program has no route of its own, so its screens live in this address as `?program=<part>`.
    const program = params.get("program");
    const say = (msg: string, ok = true) => showToast(msg, ok ? "var(--win)" : "var(--warn)");
    return (
      <>
        {program !== null ? (
          <ProgramScreens productId={id} productName={productName} part={program} say={say} />
        ) : (
          <PartnersScreen
            id={id}
            productName={productName}
            data={data}
            totals={totals}
            loading={loading}
            notFound={notFound}
            err={err}
            tab={tab}
            setTab={setTab}
            query={query}
            setQuery={setQuery}
            filterRows={filterRows}
            busyId={busyId}
            onApprove={approve}
            onReject={reject}
            onAdd={openInvite}
            onChanged={() => reload().catch(() => {})}
          />
        )}
        {inviteOpen && (
          <AddPartnerModal
            productId={id}
            productName={productName ?? "your product"}
            productSlug={productSlug}
            onClose={() => setInviteOpen(false)}
            onInvited={onInvited}
            onCopied={(label, ok) => say(copiedNote(label, ok), ok)}
          />
        )}
        <SayToast toast={{ msg: toast.msg, on: toast.show, done: toast.color !== "var(--warn)" }} />
      </>
    );
  }

  return (
    <section className="partners view-wrap">
      <button
        type="button"
        className="back"
        onClick={() => navigate(`/products/${encodeURIComponent(id)}`)}
      >
        <BackIcon />
        {backLabel}
      </button>

      <div className="head">
        <div>
          <div className="eyebrow">{productName ? `${productName} · Partners` : "Partners"}</div>
          <h1>Your affiliate program</h1>
          <p className="lede">
            People promote {name}. You reward real customers. Flashadz keeps the count.
          </p>
        </div>
        <div className="head-actions">
          <button type="button" className="btn ghost" onClick={openSettings}>
            Settings
          </button>
          <button type="button" className="btn ghost" onClick={openInvite}>
            + Add partners
          </button>
          <button type="button" className="btn" onClick={openPayrun}>
            Pay partners
          </button>
        </div>
      </div>

      <div className="setrow">
        <span className="setnote">
          You pay partners directly with the details they give you. Flashadz tracks who is owed and
          records who you have paid.
        </span>
      </div>

      {loading && <div className="load-note">Loading partners…</div>}

      {!loading && notFound && (
        <div className="missing">
          <h3>Product not found</h3>
          <p>This product does not exist, or it belongs to another account.</p>
        </div>
      )}

      {!loading && err && !notFound && (
        <div className="load-note">Couldn't load partners: {err}</div>
      )}

      {!loading && !err && !notFound && data && totals && (
        <>
          {totals.activeCount === 0 && totals.pendingCount === 0 ? (
            <div className="es">
              <div className="es-ic" aria-hidden="true">
                &#9671;
              </div>
              <h3>No partners yet</h3>
              <p>
                Invite people to promote {name}. Send an email, share a join link, or hand out a QR
                code. Everyone who joins shows up here.
              </p>
              <div className="es-actions">
                <button type="button" className="btn" onClick={openInvite}>
                  + Add partners
                </button>
                <button type="button" className="btn ghost" onClick={openSettings}>
                  Set the reward
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="pt-kpis">
                <div className="pt-kpi">
                  <div className="k">Active partners</div>
                  <div className="v">{num(totals.activeCount)}</div>
                  <div className="d">
                    {totals.pendingCount > 0
                      ? `${num(totals.pendingCount)} pending review`
                      : "none pending"}
                  </div>
                </div>
                <div className="pt-kpi">
                  <div className="k">Registrations</div>
                  <div className="v">{num(totals.regs)}</div>
                  <div className="d">via partners</div>
                </div>
                <div className="pt-kpi">
                  <div className="k">Sales</div>
                  <div className="v">{num(totals.sales)}</div>
                  <div className="d">paying customers</div>
                </div>
                <button
                  type="button"
                  className="pt-kpi partners-owed"
                  onClick={openPayrun}
                  aria-label="Owed to partners, open the pay run"
                >
                  <div className="k">Owed to partners</div>
                  <div className="v">{money(totals.owedCents)}</div>
                  <div className={`d${totals.readyCents > 0 ? " ready" : ""}`}>
                    {totals.readyCents > 0
                      ? `${money(totals.readyCents)} ready, pay now`
                      : "nothing ready yet"}
                  </div>
                </button>
              </div>

              <div className="pt-bar">
                <div className="tabs" role="tablist" aria-label="Partner status">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "active"}
                    className={tab === "active" ? "on" : ""}
                    onClick={() => setTab("active")}
                  >
                    Active <span className="cnt">{num(totals.activeCount)}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "pending"}
                    className={tab === "pending" ? "on" : ""}
                    onClick={() => setTab("pending")}
                  >
                    Pending review <span className="cnt">{num(totals.pendingCount)}</span>
                  </button>
                </div>
                <div className="pt-filters">
                  <div className={`searchbox${searchOpen || query ? " open" : ""}`}>
                    <SearchIcon />
                    <input
                      className="search-in"
                      placeholder="Search partners"
                      value={query}
                      aria-label="Search partners"
                      onFocus={() => setSearchOpen(true)}
                      onBlur={() => setSearchOpen(false)}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {tab === "active" ? (
                <div className="pt-panel">
                  <div className="panel-head">
                    <h3>Partners</h3>
                    <span className="pill pending">Last-click · 30-day window</span>
                  </div>
                  <div className="tscroll">
                    <table className="ptable">
                      <thead>
                        <tr>
                          <th>Partner</th>
                          <th className="num">Clicks</th>
                          <th className="num">Regs</th>
                          <th className="num">Sales</th>
                          <th className="num">Earned</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterRows(data.active).map((item) => (
                          <ActiveRow
                            key={item.partner.id}
                            item={item}
                            onOpen={(pid) => navigate(`/partners/${encodeURIComponent(pid)}`)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {data.active.length === 0 && (
                    <div className="tab-empty">No active partners yet.</div>
                  )}
                  {data.active.length > 0 && filterRows(data.active).length === 0 && (
                    <div className="tab-empty">No partners match your search.</div>
                  )}
                </div>
              ) : (
                <div className="pt-panel">
                  <div className="panel-head">
                    <h3>Waiting for your approval</h3>
                    <span className="setnote">Applied and signed. Review, then approve.</span>
                  </div>
                  <div className="tscroll">
                    <table className="ptable">
                      <thead>
                        <tr>
                          <th>Applicant</th>
                          <th className="num">Applied</th>
                          <th>Agreement</th>
                          <th className="acts" aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {filterRows(data.pending).map((item) => (
                          <PendingRow
                            key={item.partner.id}
                            item={item}
                            busy={busyId === item.partner.id}
                            onApprove={approve}
                            onReject={reject}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {data.pending.length === 0 && (
                    <div className="tab-empty">No one is waiting for approval.</div>
                  )}
                  {data.pending.length > 0 && filterRows(data.pending).length === 0 && (
                    <div className="tab-empty">No applicants match your search.</div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {inviteOpen && (
        <AddPartnerModal
          productId={id}
          productName={productName ?? "your product"}
          productSlug={productSlug}
          onClose={() => setInviteOpen(false)}
          onInvited={onInvited}
        />
      )}

      {settingsOpen && (
        <ProgramSettingsModal
          productId={id}
          productName={productName ?? "your product"}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => showToast("Program saved", "var(--win)")}
          onEditAgreement={openAgreementBuilder}
          onOpenLibrary={openLibrary}
        />
      )}

      <div className={`pt-toast ${toast.show ? "show" : ""}`}>
        <span className="tdot" style={{ background: toast.color }} />
        <span className="msg">{toast.msg}</span>
      </div>
    </section>
  );
}
